import React, { useState } from "react";
import { View, Text, StyleSheet, Pressable, Linking } from "react-native";
import { Check, ChevronDown, Search } from "lucide-react";
import { MotiView, AnimatePresence } from "moti";
import { colors, spacing, radii, typographyStyles } from "../../../styles/design-system";
import { researchStages, STAGE_SUBTITLES, DEFAULT_STAGE_SUBTITLE, type ResearchStageId } from "./researchStages";
import type { DeepResearchProgressProps } from "./types";
import { getSourceEmoji } from "./utils";

const ACTIVE_TOOL_LABELS: Record<string, string> = {
  code_interpreter: "Using code interpreter",
  web_search_preview: "Searching the web",
};

export function DeepResearchProgress({
  currentStage,
  progress,
  sourcesFound,
  elapsedSeconds,
  progressMode = "active",
  sourcesCount,
  activeTool,
  onCancel,
}: DeepResearchProgressProps) {
  const [showSources, setShowSources] = useState(false);
  const isPreparing = progressMode === "preparing";
  const currentStageIndex = researchStages.findIndex((s) => s.id === currentStage);
  const safeStageIndex = currentStageIndex >= 0 ? currentStageIndex : 0;
  const effectiveSourcesCount = Math.max(sourcesFound.length, sourcesCount ?? 0);
  const elapsedLabel =
    elapsedSeconds != null && elapsedSeconds >= 0 && !isPreparing
      ? elapsedSeconds >= 60
        ? `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s`
        : `${elapsedSeconds}s`
      : null;
  const baseSubtitle =
    (currentStage in STAGE_SUBTITLES ? STAGE_SUBTITLES[currentStage as ResearchStageId] : null) ??
    DEFAULT_STAGE_SUBTITLE;
  const subtitle = isPreparing
    ? "Preparing your research session…"
    : currentStage === "searching"
      ? `${baseSubtitle}${effectiveSourcesCount > 0 ? ` (${effectiveSourcesCount} found)` : ""}`
      : baseSubtitle;
  const activeToolLabel = activeTool ? (ACTIVE_TOOL_LABELS[activeTool] ?? `Using ${activeTool}`) : null;

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <View style={styles.iconBox}>
            <Search size={16} color={colors.brand[600]} />
          </View>
          <View style={styles.headerText}>
            <Text style={styles.title}>Deep Research</Text>
            <Text style={styles.subtitle}>{subtitle}</Text>
            {activeToolLabel != null && <Text style={styles.activeToolLine}>{activeToolLabel}</Text>}
          </View>
        </View>
        <View
          style={styles.progressTrack}
          accessibilityRole="progressbar"
          accessibilityLabel={
            isPreparing
              ? "Deep research, preparing"
              : `Deep research progress, ${Math.min(100, Math.max(0, Math.round(progress)))}% complete`
          }
          accessibilityValue={isPreparing ? undefined : { now: progress, min: 0, max: 100 }}>
          {isPreparing ? null : (
            <MotiView
              key="dr-active-fill"
              style={[styles.progressFill, { width: `${Math.min(100, Math.max(0, progress))}%` }]}
              animate={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              transition={{ type: "timing", duration: 900 }}
            />
          )}
        </View>
      </View>

      <View style={styles.stages}>
        {researchStages.map((stage, idx) => {
          const isComplete = !isPreparing && idx < safeStageIndex;
          const isCurrent = isPreparing ? idx === 0 : idx === safeStageIndex;
          const isPending = isPreparing ? idx > 0 : idx > safeStageIndex;
          const stageLabel = isPreparing && idx === 0 ? "Preparing…" : stage.label;
          return (
            <View key={stage.id} style={styles.stageRow}>
              <View
                style={[
                  styles.stageDot,
                  isComplete && styles.stageDotComplete,
                  isCurrent && styles.stageDotCurrent,
                  isPending && styles.stageDotPending,
                ]}>
                {isComplete ? (
                  <Check size={12} color={colors.white} />
                ) : isCurrent ? (
                  <View style={styles.pulseDot} />
                ) : (
                  <View style={styles.pendingDot} />
                )}
              </View>
              <Text
                style={[
                  styles.stageLabel,
                  isComplete && styles.stageLabelComplete,
                  isCurrent && styles.stageLabelCurrent,
                  isPending && styles.stageLabelPending,
                ]}>
                {stageLabel}
                {stage.id === "searching" && sourcesFound.length > 0 && (
                  <Text style={styles.sourcesCount}> ({sourcesFound.length} found)</Text>
                )}
              </Text>
            </View>
          );
        })}
      </View>

      {sourcesFound.length > 0 && (
        <View style={styles.sourcesSection}>
          <Pressable
            style={({ pressed }) => [styles.sourcesToggle, pressed && styles.sourcesTogglePressed]}
            onPress={() => setShowSources(!showSources)}
            accessibilityRole="button"
            accessibilityLabel={`Sources ${sourcesFound.length}. ${showSources ? "Collapse" : "Expand"}`}>
            <Text style={styles.sourcesToggleText}>Sources ({sourcesFound.length})</Text>
            <MotiView
              animate={{ rotate: showSources ? "180deg" : "0deg" }}
              transition={{ type: "timing", duration: 200 }}>
              <ChevronDown size={14} color={colors.grey[600]} />
            </MotiView>
          </Pressable>
          <AnimatePresence>
            {showSources && (
              <MotiView
                from={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "timing", duration: 200 }}
                style={styles.sourcesListWrap}>
                {sourcesFound.map((source) => (
                  <Pressable
                    key={source.id}
                    style={({ pressed }) => [styles.sourceRow, pressed && styles.sourceRowPressed]}
                    onPress={() => source.url && Linking.openURL(source.url)}
                    accessibilityRole="link"
                    accessibilityLabel={source.title || source.url}>
                    <Text style={styles.sourceEmoji}>{getSourceEmoji(source)}</Text>
                    <Text style={styles.sourceTitle} numberOfLines={1}>
                      {source.title || source.url}
                    </Text>
                  </Pressable>
                ))}
              </MotiView>
            )}
          </AnimatePresence>
        </View>
      )}

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          {isPreparing
            ? "Connecting and starting your research…"
            : elapsedLabel != null
              ? `Still researching (${elapsedLabel})`
              : "This may take a few minutes..."}
        </Text>
        {onCancel && (
          <Pressable
            style={({ pressed }) => [styles.cancelBtn, pressed && styles.cancelBtnPressed]}
            onPress={onCancel}
            accessibilityRole="button"
            accessibilityLabel="Cancel">
            {(state) => {
              const hovered = (state as { hovered?: boolean }).hovered;
              return <Text style={[styles.cancelText, hovered && styles.cancelTextHovered]}>Cancel</Text>;
            }}
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    marginVertical: spacing.sm,
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  header: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  iconBox: {
    width: 32,
    height: 32,
    borderRadius: radii.lg,
    backgroundColor: colors.brand[50],
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    flex: 1,
  },
  title: {
    ...typographyStyles.textSm,
    fontWeight: "600",
    color: colors.grey[900],
  },
  subtitle: {
    ...typographyStyles.textXs,
    color: colors.grey[500],
  },
  activeToolLine: {
    ...typographyStyles.textXs,
    color: colors.brand[600],
    marginTop: 2,
  },
  progressTrack: {
    height: 6,
    backgroundColor: colors.grey[100],
    borderRadius: radii.full,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: colors.brand[500],
    borderRadius: radii.full,
  },
  stages: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
  },
  stageRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  stageDot: {
    width: 20,
    height: 20,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
  },
  stageDotComplete: {
    backgroundColor: colors.success[500],
  },
  stageDotCurrent: {
    backgroundColor: colors.brand[100],
  },
  stageDotPending: {
    backgroundColor: colors.grey[100],
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.brand[500],
    opacity: 0.9,
  },
  pendingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.grey[300],
  },
  stageLabel: {
    ...typographyStyles.textSm,
    flex: 1,
  },
  stageLabelComplete: {
    color: colors.grey[500],
  },
  stageLabelCurrent: {
    color: colors.grey[900],
    fontWeight: "500",
  },
  stageLabelPending: {
    color: colors.grey[400],
  },
  sourcesCount: {
    color: colors.grey[400],
  },
  sourcesSection: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  sourcesToggle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  sourcesTogglePressed: {
    backgroundColor: colors.grey[50],
  },
  sourcesToggleText: {
    ...typographyStyles.textSm,
    color: colors.grey[600],
  },
  sourcesListWrap: {
    overflow: "hidden",
  },
  sourceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
  },
  sourceRowPressed: {
    backgroundColor: colors.grey[50],
  },
  sourceEmoji: {
    fontSize: 12,
  },
  sourceTitle: {
    ...typographyStyles.textXs,
    color: colors.grey[700],
    flex: 1,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
    backgroundColor: colors.grey[50],
  },
  footerText: {
    ...typographyStyles.textXs,
    color: colors.grey[500],
  },
  cancelBtn: {
    paddingVertical: spacing.xxs,
    paddingHorizontal: spacing.xs,
  },
  cancelBtnPressed: {
    opacity: 0.8,
  },
  cancelText: {
    ...typographyStyles.textXs,
    color: colors.grey[500],
  },
  cancelTextHovered: {
    color: colors.grey[700],
  },
});
